package org.hs_soft.runmynesto.pages.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.hs_soft.runmynesto.domain.config.Dimen
import org.hs_soft.runmynesto.domain.config.FontFamily
import org.hs_soft.runmynesto.domain.config.FontSizes
import org.hs_soft.runmynesto.domain.config.Theme
import org.hs_soft.runmynesto.domain.util.Constants
import org.hs_soft.runmynesto.domain.util.Res
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

@Composable
fun DropDownMultiSelect(
    modifier: Modifier = Modifier,
    options: List<Pair<Int, String>>,
    height: CSSSizeValue<CSSUnit.px> = Dimen.outlinedEditTextParentHeight,
    label: String,
    fillMaxWidth: Boolean = false,
    disable: Boolean = false,
    selectedIds: List<Int>,
    fontSize: CSSSizeValue<CSSUnit.px> = FontSizes.mediumFontSize,
    fontWeight: FontWeight = FontWeight.Normal,
    onSelectionChange: (List<Int>) -> Unit,
    itemWidth: CSSSizeValue<CSSUnit.px> = Dimen.mediumEditTextWidth,
    borderRadius: CSSSizeValue<CSSUnit.px> = Dimen.lowBorderRadius,
    iconPath: String? = null,
    id: String? = null,
    isFixed: Boolean = false,
) {
    val isHovered = remember { mutableStateOf(false) }
    val isExpanded = remember { mutableStateOf(false) }
    val isFocused = remember { mutableStateOf(false) }
    val isDisabled = options.isEmpty() || disable
    val labelMargin = Dimen.mediumPadding
    val dropdownMenuCoords = remember { mutableStateOf<Pair<Float, Float>?>(null) }

    LaunchedEffect(isExpanded.value) {
        isFocused.value = isExpanded.value
    }

    if (isExpanded.value){
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
                .right(0.px)
                .bottom(0.px)
                .fillMaxSize()
                .onClick {
                    isExpanded.value=false
                }
        )
    }

    Box(
        modifier = modifier
            .onFocusOut { isFocused.value = false }
            .onFocusIn { isFocused.value = true }
            .onMouseOver { if (!isDisabled) isHovered.value = true }
            .onMouseLeave { if (!isDisabled) isHovered.value = false }
            .height(height)
            .thenIf(fillMaxWidth) { Modifier.fillMaxWidth() }
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .align(Alignment.TopStart)
                .margin(left = Dimen.mediumPadding)
                .zIndex(Constants.ZIndex.smallZIndex)
                .padding(leftRight = Dimen.smallPadding)
                .backgroundColor(Theme.White.rgb)
                .fontWeight(FontWeight.Normal)
                .color(
                    when {
                        isDisabled -> Theme.TextDisabled.rgb
                        isFocused.value -> Theme.PrimaryMain.rgb
                        else -> Theme.TextSecondary.rgb
                    }
                )
                .fontFamily(FontFamily.Jakarta)
                .fontSize(FontSizes.semiMediumFontSize)
        )

        Box(
            modifier = Modifier
                .display(DisplayStyle.Flex)
                .align(Alignment.BottomStart)
                .flexDirection(FlexDirection.Column)
                .width(itemWidth)
                .thenIf(fillMaxWidth) { Modifier.fillMaxWidth() }
                .height(Dimen.mediumOutlinedEditTextHeight)
                .fontFamily(FontFamily.Jakarta)
                .borderRadius(borderRadius)
                .backgroundColor(Theme.Transparent.rgb)
                .position(Position.Relative)
        ) {
            if (iconPath != null) {
                VectorIcon(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .zIndex(Constants.ZIndex.mediumZIndex)
                        .left(Dimen.mediumPadding)
                        .top(55.percent)
                        .styleModifier {
                            property("transform", "translateY(-50%)")
                            property("pointer-events", "none")
                        },
                    pathData = iconPath,
                    width = Dimen.mediumIconSize,
                    height = Dimen.mediumIconSize,
                    color = Theme.ActionActive.hex,
                )
            }

            VectorIcon(
                modifier = Modifier
                    .position(Position.Absolute)
                    .zIndex(Constants.ZIndex.mediumZIndex)
                    .right(Dimen.mediumPadding)
                    .top(50.percent)
                    .attrsModifier {
                        style {
                            property("transform-origin", "center center")
                            property(
                                "transform",
                                if (isExpanded.value)
                                    "translateY(-50%) rotate(180deg)"
                                else "translateY(-50%) rotate(0deg)"
                            )
                            property("pointer-events", "none")
                        }
                    },
                pathData = Res.PathIcon.arrowBelow,
                width = Dimen.mediumIconSize,
                height = Dimen.mediumIconSize,
                color = if (isDisabled) Theme.TextDisabled.hex
                else if (isFocused.value) Theme.PrimaryMain.hex
                else Theme.ActionActive.hex,
            )

            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(Dimen.mediumOutlinedEditTextHeight)
                    .id(id ?: "")
                    .onClick {
                        if (!isDisabled) {
                            isExpanded.value = !isExpanded.value
                            isFocused.value = !isFocused.value

                            if (isFixed) {
                                window.setTimeout({
                                    val el = document.getElementById(id ?: "") as? HTMLElement
                                    if (el != null) {
                                        val rect = el.getBoundingClientRect()
                                        dropdownMenuCoords.value = Pair(rect.left.toFloat(), rect.bottom.toFloat())
                                    }
                                }, 10)
                            }
                        }
                    }
                    .styleModifier {
                        property(
                            "border-width", if (isFocused.value)
                                "${Dimen.mediumThickness.value}px"
                            else "${Dimen.smallThickness.value}px"
                        )
                        property("border-style", "solid")
                        property(
                            "border-color", when {
                                isDisabled -> Theme.TextDisabled.hex
                                isFocused.value -> Theme.PrimaryMain.hex
                                isHovered.value -> Theme.TextPrimary.hex
                                else -> Theme.InputEnabledBorder.hex
                            }
                        )
                        property("border-radius", "$borderRadius")
                        property("background-color", Theme.White.hex)
                        property("box-sizing", "border-box")
                        property("font-family", FontFamily.Jakarta)
                        property("font-size", "${fontSize}px")
                        property("font-weight", fontWeight.toString())
                        property("cursor", if (isDisabled) "not-allowed" else "pointer")
                        property("display", "flex")
                        property("align-items", "center")
                        property(
                            "padding-left", if (iconPath != null)
                                "${Dimen.mediumPadding.value + Dimen.mediumIconSize}px"
                            else "${labelMargin.value}px"
                        )
                        property(
                            "padding-right",
                            "${Dimen.largePadding.value + Dimen.mediumIconSize}px"
                        )
                    }
            ) {
                if (selectedIds.isNotEmpty()) {
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("left", if (iconPath != null)
                                    "${Dimen.mediumPadding.value + Dimen.mediumIconSize}px"
                                else
                                    "${labelMargin.value}px")
                                property("top", "50%")
                                property("transform", "translateY(-50%)")
                            }
                    ) {
                        SpanText(
                            text = if (selectedIds.isNotEmpty()) {
                                "${options.find { it.first == selectedIds.first() }?.second.orEmpty()}${if (selectedIds.size > 1) ", ..." else ""}"
                            } else "",

                            modifier = Modifier
                                .fontSize(fontSize)
                                .color(Theme.TextPrimary.rgb)
                                .fontWeight(fontWeight)
                                .fontFamily(FontFamily.Jakarta)
                        )
                    }
                }
            }

            if (isExpanded.value && !isDisabled && (!isFixed || dropdownMenuCoords.value != null)) {
                Box(
                    modifier = Modifier
                        .padding(topBottom = Dimen.normalPadding)
                        .zIndex(Constants.ZIndex.extraLargeZIndex)
                        .top(Dimen.mediumOutlinedEditTextHeight.plus(Dimen.normalPadding))
                        .width(if (fillMaxWidth) 100.percent else itemWidth)
                        .styleModifier {
                            if (isFixed) {
                                val coords = dropdownMenuCoords.value!!
                                property("position", "fixed")
                                property("left", "${coords.first}px")
                                property("top", "${coords.second}px")
                            } else {
                                property("position", "absolute")
                            }
                            property("background-color", Theme.White.hex)
                            property("border-radius", "${Dimen.semiLargeRadius}")
                            property("box-shadow", "0px 3px 15px 0px ${Theme.ActionFocused.rgb}")
                            property("max-height", "250px")
                            property("overflow-y", "auto")
                            property("overflow-x", "hidden")
                            property("border", "1px solid ${Theme.ActionFocused.hex}")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "${Theme.TextSecondary.hex} transparent")
                        }
                ) {
                    Column(modifier = Modifier.fillMaxWidth()) {
                        options.forEachIndexed { index, option ->
                            val isSelected = selectedIds.contains(option.first)
                            val itemHoveredState = remember { mutableStateOf(false) }

                            Div(
                                attrs = {
                                    style {
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("width", "100%")
                                        property("height", "${Dimen.mediumOutlinedEditTextHeight.value}px")
                                        property("padding-left", "${labelMargin.value}px")
                                        property("padding-right", "${Dimen.mediumPadding.value}px")
                                        property(
                                            "background-color",
                                            when {
                                                isSelected -> Theme.PrimaryStatesSelected.hex
                                                itemHoveredState.value -> Theme.PrimaryStatesHover.hex
                                                else -> Theme.White.hex
                                            }
                                        )
                                        property("cursor", "pointer")
                                        property("user-select", "none")
                                    }

                                    onMouseOver { itemHoveredState.value = true }
                                    onMouseOut { itemHoveredState.value = false }
                                    onClick {
                                        val newSelection = selectedIds.toMutableList()
                                        if (isSelected) {
                                            newSelection.remove(option.first)
                                        } else {
                                            newSelection.add(option.first)
                                        }
                                        onSelectionChange(newSelection)
                                    }
                                }
                            ) {
                                SpanText(
                                    text = option.second,
                                    modifier = Modifier
                                        .fontSize(fontSize)
                                        .color(Theme.TextPrimary.rgb)
                                        .fontWeight(fontWeight)
                                        .fontFamily(FontFamily.Jakarta)
                                )
                            }

//                            if (index < options.size - 1) {
//                                Separator()
//                            }
                        }
                    }
                }
            }
        }
    }
}